import React, {PureComponent} from 'react';
import {Formik, Form, FieldArray, yupToFormErrors} from 'formik';
import Yup from '../../yup';
import flatten from 'lodash/flatten';
import map from 'lodash/map';
import {injectIntl} from 'react-intl';
import messages from '../../../res/translations/messages';
import PageType from './editor/PageType';
import SurveyEditorItem from './editor/SurveyEditorItem';
import FooterToolbar from './FooterToolbar';
import typeValidatorSchemas from '../../services/typeValidatorSchemas';
import {
    getPreScriptValidatorSchema,
    getPostScriptValidatorSchema,
    getOptionsScriptValidatorSchema,
    getRowsScriptValidatorSchema,
    getAlertFilterValidatorSchema} from '../../services/logicValidatorSchemas';
import ChannelFields from './ChannelsFields';
import SurveyCloudEventsSettingsForm from './SurveyCloudEventsSettingsForm';
import SurveyEditorItemHeader from './editor/SurveyEditorItemHeader';
import SurveyEditorSection from './editor/SurveyEditorSection';
import SurveyEditorBox from './editor/SurveyEditorBox';
import SurveySettingsSection from './SurveySettingsSection';
import SurveySendingSection from './SurveySendingSection';
import SurveyEditorErrorScroller from './editor/SurveyEditorErrorScroller';
import SurveyConsoleSettingsForm from './SurveyConsoleSettingsForm';
import SurveyParametersSettingsForm from './SurveyParametersSettingsForm';
import SurveyExtraFieldsForm from './SurveyExtraFieldsForm';
import SurveyAlertsForm from './SurveyAlertsForm';
import {sendProviderParamsSchema} from './sendProvider/sendProviderParamsSchema';
import {TRACKERS_ARCHIVE_OPTIONS} from '../../constants';
import {isExpiryDayPast, toExpiryDay} from '../../services/expiryAbsoluteDate';
import {constants} from '@neosvoc/nv-platform-core-common';


const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const invalidEmailAddress = (value) => ({key: 'invalidEmailAddress', params: value});
const isValidAddressList = (value) => {
    if (!value) {
        return false;
    }
    const addresses = value.split(';').map((address) => address.trim()).filter((address) => address !== '');
    return addresses.length > 0 && addresses.every((address) => EMAIL_REGEX.test(address));
};

const SurveyEditSchema = Yup.object().shape({
    name: Yup.string().required(),
    title: Yup.string().required(),
    intro: Yup.string(),
    locale: Yup.string().required(),
    published: Yup.boolean().required(),
    trackerUrl: Yup.string().url(),
    cloudEventsEnabled: Yup.boolean(),
    cloudEventsSettings: Yup.array().defined().of(Yup.object().shape({
        events: Yup.array().of(Yup.string().oneOf(Object.values(constants.CLOUD_EVENTS))).min(1).required(),
        url: Yup.string().url().required(),
        token: Yup.string(),
        identifier: Yup.string(),
    })),
    sendProvider: Yup.string(),
    sendProviderParams: sendProviderParamsSchema,
    tags: Yup.array().of(Yup.string()),
    editor: Yup.object().shape({
        pages: Yup.array().of(
            Yup.lazy((page) => (
                Yup.object().shape({
                    preScripts: Yup.array().of(getPreScriptValidatorSchema(page.id)),
                    id: Yup.string().required(),
                    items: Yup.array().of(Yup.lazy((item) => typeValidatorSchemas[item.type](item, page))), // this is needed for reversed nested validation
                    postScripts: Yup.array().of(getPostScriptValidatorSchema(page.id)),
                    optionsScripts: Yup.array().of(getOptionsScriptValidatorSchema(page.id)),
                    rowsScripts: Yup.array().of(getRowsScriptValidatorSchema(page.id)),
                })
            )),
        ),
    }),
    trackerArchiveFrequency: Yup.string().required().oneOf(Object.values(TRACKERS_ARCHIVE_OPTIONS)),
    trackerArchiveFrequencyValue: Yup.number().when('trackerArchiveFrequency', {
        is: (val) => val === 'trackerCreated',
        then: Yup.number().required().min(1),
        otherwise: Yup.number().required().min(0),
    }),
    expiryEnabled: Yup.boolean(),
    // Both expiry fields are optional: an empty field means no expiry of that kind
    expiryDays: Yup.number().when('expiryEnabled', {
        is: true,
        then: Yup.number().min(1),
    }),
    expiryAbsoluteDate: Yup.string().nullable().when('expiryEnabled', {
        is: true,
        then: Yup.string().nullable()
            .test('is-not-past', () => ({key: 'expiryAbsoluteDatePast'}), (value) => !isExpiryDayPast(value)),
    }),
    consoleButtons: Yup.array().defined().of(Yup.object().shape({
        url: Yup.string().url().required(),
        label: Yup.string().max(30).required(),
    })),

    parameters: Yup.array().defined().of(Yup.object().shape({
        name: Yup.string().required(), // check URL safe
        unique: Yup.boolean(),
        mandatory: Yup.boolean(),
    })),

    extraFields: Yup.array().defined().of(Yup.object().shape({
        name: Yup.string(),
    })),

    alerts: Yup.array().defined().of(Yup.object().shape({
        filter: getAlertFilterValidatorSchema(),
        subject: Yup.string().required(),
        message: Yup.string().required(),
        address: Yup.string().required().test('is-valid-address-list', invalidEmailAddress, isValidAddressList),
    })),
});

class SurveyEditForm extends PureComponent {
    constructor(props) {
        super(props);

        this.onSubmit = this.onSubmit.bind(this);
        this.validate = this.validate.bind(this);
        this.ensureRequiredProps = this.ensureRequiredProps.bind(this);
    }

    ensureRequiredProps(values) {
        // Verifica e aggiunge required: true agli items di tipo diverso da TEXT_1
        if (values.editor && values.editor.pages) {
            values.editor.pages.forEach((page) => {
                if (page.items) {
                    page.items.forEach((item) => {
                        if (item.type !== constants.EDITOR_TEMPLATES.TEXT_1 && !('required' in item)) {
                            item.required = true;
                        }
                        if ([constants.EDITOR_TEMPLATES.MULTIPLE_NUMBER_2, constants.EDITOR_TEMPLATES.NUMBER_2].includes(item.type) &&
                        !('componentType' in item)) {
                            item.componentType = 'slider';
                        }
                    });
                }
            });
        }
        return values;
    }

    onSubmit(values, actions) {
        values = Object.assign({}, values, {published: values.published === true || values.published === 'true'});
        this.props.saveCallback(values);
        actions.resetForm(values);
    }

    async validate(values) {
        // const pageItems = flatten(map(values.editor.pages, 'items'));
        const pageItems = flatten(
            map(values.editor.pages,
                (page, index) =>
                    (map(page.items, (item) =>(
                        {
                            pageIndex: index,
                            pageId: page.id,
                            ...item,
                        })))));

        const pages = map(values.editor.pages, (page) => ({
            id: page.id,
            choices: map(flatten(page.items.map((item) => item.choices)), 'id'),
            rows: map(flatten(page.items.map((item) => item.rows)), 'id'),
        }));
        try {
            await SurveyEditSchema.validate(values, {abortEarly: false, context: {pageItems, pages,
                parameters: values.parameters, extraFields: values.extraFields}});
        } catch (err) {
            console.log('***ERROR DURING VALIDATE***', err);
            throw yupToFormErrors(err);
        }
    }

    render() {
        const {intl, item, error, organization} = this.props;
        if (!item.trackerArchiveFrequency) {
            if (organization && organization.trackerArchiveFrequency) {
                item.trackerArchiveFrequency = organization.trackerArchiveFrequency;
                item.trackerArchiveFrequencyValue = organization.trackerArchiveFrequencyValue;
            } else {
                item.trackerArchiveFrequency = 'never';
                item.trackerArchiveFrequencyValue = 0;
            }
        }
        if (!item.expiryEnabled) {
            item.expiryEnabled = false;
        }
        // Empty input when the expiry days are not set (0 has never been a valid value)
        item.expiryDays = item.expiryDays || '';
        item.expiryAbsoluteDate = toExpiryDay(item.expiryAbsoluteDate);
        return <Formik
            initialValues={this.ensureRequiredProps(item)}
            enableReinitialize={true}
            validate={this.validate}
            onSubmit={this.onSubmit}
            validateOnBlur={true}
            validateOnChange={false}
        >
            {({setFieldValue, values, dirty, errors, isSubmitting, isValidating, touched}) => (
                <Form>
                    <SurveyEditorErrorScroller errors={errors} isSubmitting={isSubmitting} isValidating={isValidating} />
                    <div className="nv-list">
                        <SurveyEditorItem name="settings" header={<SurveyEditorItemHeader values={values} setFieldValue={setFieldValue} />}>
                            <SurveySettingsSection
                                values={values} errors={errors} touched={touched} setFieldValue={setFieldValue} intl={intl} />
                        </SurveyEditorItem>
                        <SurveyEditorSection name="sending" title={messages.surveys.form.title.sending}>
                            <SurveySendingSection
                                values={values} errors={errors} touched={touched} setFieldValue={setFieldValue}
                                organization={organization} intl={intl} validationSchema={SurveyEditSchema} />
                        </SurveyEditorSection>
                        <SurveyEditorSection name="events" title={messages.surveys.form.title.events}>
                            {organization.allowEvents && (
                                <SurveyEditorBox title={messages.surveys.form.title.cloudEventsSettings}>
                                    <SurveyCloudEventsSettingsForm />
                                </SurveyEditorBox>
                            )}
                            <SurveyEditorBox title={messages.surveys.form.title.alerts}>
                                <SurveyAlertsForm />
                            </SurveyEditorBox>
                        </SurveyEditorSection>
                        <SurveyEditorSection name="consoleSettings" title={messages.surveys.form.title.consoleSettings}>
                            <SurveyConsoleSettingsForm />
                        </SurveyEditorSection>
                        <SurveyEditorSection name="extraParameters" title={messages.surveys.form.title.extraParameters}>
                            <SurveyEditorBox title={messages.surveys.form.title.paramQs}>
                                <SurveyParametersSettingsForm />
                            </SurveyEditorBox>
                            <SurveyEditorBox title={messages.surveys.form.title.paramTracker}>
                                <SurveyExtraFieldsForm />
                            </SurveyEditorBox>
                        </SurveyEditorSection>
                        <ChannelFields values={values.channels} setFieldValue={setFieldValue} surveyId={values.id} />
                        <FieldArray name="editor.pages" component={PageType} />
                    </div>
                    <FooterToolbar dirty={dirty} error={error} />
                </Form>
            )}
        </Formik>;
    }
}

export {SurveyEditSchema};
export default injectIntl(SurveyEditForm);
